import { createFilters, createUrlEnumFilter, createUrlFilter, createUrlObjectFilter } from './filters-core';
import { DateTime } from 'luxon';
import {
  ActiveShiftsDashboard,
  AdminTasksUrlFilters,
  DriverTasksUrlFilters,
  ManagerTasksUrlFilters,
  ShiftCardActiveModal,
  TopRecoveryAgentDetails,
  TopRecoveryAgentsModalProp,
  TopSpotterModalData,
  ViewTab,
} from './types';

import {
  CAMERA_CARS_RANK_OPTIONS_V2,
  CameraCarsTabs,
  SEARCH_PARAM_KEYS,
  RECOVERY_AGENTS_RANK_OPTIONS_V2,
  RecoveryTabs,
  PAGE_SIZE,
  SEARCH_PARAMS_VALUES,
  ReportDailyMetric,
  ModalType,
  DailyMapTabType,
} from '../../constants/app.constants';
import { DateRangeType } from '@rapid-recovery-agency-inc/sloth-ui-web';
import {
  CameraHitsStatus,
  CasesUnitReachStatus,
  HitType,
  SortOrder,
  ChecklistQuestionCategory,
  RolesType,
  PeopleInShiftsRole,
  SpotType,
  ChecklistSetCategory,
  StorageFilterType,
  TaskStatusGroupListOrderBy,
  InfractionsSortKey,
  PeopleInShiftsFields,
  SpottedVehicleReportSortKey,
  RepossessionsSortKey,
} from '@rapid-recovery-agency-inc/insightt-graphql';
import {
  CaseStatusFilter,
  ClientType,
  OrderTypeFilters,
  SuscriptionViews,
  UnitReachStatus,
  UserStatus,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { DateRangeSelector, DayMenuOption, ReportMenuOption } from '../../modules/reports/reports-constants';
import { HitsDataModalPropV2 } from '../../modules/overview/top-camera-cars/DrnHitsModalV2';
import { SecuredDataModalPropV2 } from '../../modules/overview/top-camera-cars/DrnSecureModalV2';
import {
  ChecklistFilter,
  OrderTypeCategory,
  TabsDayStored,
  UserManagementSortKey,
} from '@rapid-recovery-agency-inc/insightt-graphql';
import { DailyMapType } from '../../modules/reports/reports-types';
import { TimesheetsDailySortKey, TimesheetsWeeklySortKey } from '../../modules/time-sheets-2/types';
import { TopEmployeeStatsField } from '../../modules/reports-v2/employee-stats/types';
import { ScanOption } from '../../modules/lpr/constants';
import { UserDetailsTab } from '../../modules/users-v2/constants';
import { UserStatsTabType } from '../../modules/users-v2/types';
import { AssignmentsCheckedSortKey } from '../../modules/reports/assignments-checked/types';
import { ChecklistTabValue, ChecklistTabValueEnum } from '../../modules/checklists-v2/types';

/**
 * @typedef {Object} UrlFilterConfig
 * @property {string} key - The key represents the name of the URL parameter.
 *                          It is used to identify the parameter in the URL.
 *                          Example: 'startDateTime'
 * @property {function} deserialize - The deserialize function converts the raw URL parameter value into a JavaScript object.
 *                                   It is responsible for parsing and transforming the URL parameter into the desired format.
 *                                   @param {string} value - The raw URL parameter value.
 *                                   @returns {any} The transformed JavaScript object.
 *                                   Example:
 *                                   deserialize: (date) => date && dayjs(date).isValid() ? dayjs(date) : undefined,
 * @property {function} serialize - The serialize function converts the JavaScript object into a string suitable for the URL parameter.
 *                                 It is responsible for formatting the object into a string that can be appended to the URL.
 *                                 @param {any} value - The JavaScript object to be serialized.
 *                                 @returns {string} The serialized string.
 *                                 Example:
 *                                 serialize: (date) => date ? date.toString() : undefined,
 */

/**
 * createUrlFilter Function Documentation
 *
 * The createUrlFilter function is used to create a filter configuration object for managing URL parameters.
 * It is particularly useful for handling various types of data, such as dates, numbers, and strings, in URL query parameters.
 *
 * @param {UrlFilterConfig} config - The configuration object for creating the URL filter.
 * @returns {Object} The created URL filter object.
 *
 * @example
 * // Usage example:
 * const filters = createFilters({
 *   startDateTime: createUrlFilter({
 *     key: 'startDateTime',
 *     deserialize: (date) => date && dayjs(date).isValid() ? dayjs(date) : undefined,
 *     serialize: (date) => date ? date.toString() : undefined,
 *   }),
 *   // ... other filters ...
 * });
 */
export const filters = {
  modalTab: createUrlFilter({
    key: 'modalTab',
    deserialize: (modalTab) => modalTab,
    serialize: (modalTab) => modalTab,
  }),
  modalPage: createUrlFilter({
    key: 'modalPage',
    deserialize: (page) => (isNaN(Number(page)) ? 1 : Number(page)),
    serialize: (page) => (page ? page.toString() : undefined),
  }),
  redirect: createUrlFilter({
    key: 'redirect',
    deserialize: (pathName) => pathName?.replaceAll('"', ''),
    serialize: (pathName) => `"${pathName}"`,
  }),
  returnIfNoSession: createUrlFilter({
    key: 'returnIfNoSession',
    deserialize: (returnIfNoSession) => returnIfNoSession === 'true',
    serialize: (returnIfNoSession) => (returnIfNoSession ? 'true' : 'false'),
  }),
  basicDashboardTab: createUrlFilter({
    key: 'basicDashboardTab',
    deserialize: (tab) => tab,
    serialize: (tab) => tab,
  }),
  basicDashboardSwitch: createUrlFilter({
    key: 'basicDashboardSwitch',
    deserialize: (tab) => tab,
    serialize: (tab) => tab,
  }),
  basicDashboardListTab: createUrlFilter({
    key: 'basicDashboardListTab',
    deserialize: (tab) => tab,
    serialize: (tab) => tab,
  }),
  sortKey: createUrlFilter({
    key: 'sortKey',
    deserialize: (sortKey) => sortKey,
    serialize: (sortKey) => sortKey,
  }),
  sortOrder: createUrlEnumFilter<SortOrder>('sortOrder'),
  dailyTimeSheetsSortKey: createUrlEnumFilter<TimesheetsDailySortKey>('dailyTimeSheetsSortKey'),
  assignmentsCheckedSortKey: createUrlEnumFilter<AssignmentsCheckedSortKey>('assignmentsCheckedSortKey'),
  shiftManagementSortKey: createUrlEnumFilter('shiftManagementSortKey'),
  timeSheetsWeeklySortKey: createUrlEnumFilter<TimesheetsWeeklySortKey>('timeSheetsWeeklySortKey'),
  userManagementSortKey: createUrlEnumFilter<UserManagementSortKey>('userManagementSortKey'),
  userStatus: createUrlEnumFilter<UserStatus>('userStatus'),
  reachStatus: createUrlEnumFilter<CasesUnitReachStatus>('reach-status'),
  searchQuery: createUrlFilter({
    key: 'searchQuery',
    deserialize: (searchQuery) => searchQuery,
    serialize: (searchQuery) => searchQuery,
  }),
  orderType: createUrlEnumFilter<OrderTypeCategory | OrderTypeFilters>('orderType'),
  tabsDayStored: createUrlEnumFilter<TabsDayStored>('tabsDayStored'),
  startDayStored: createUrlFilter({
    key: 'startDayStored',
    deserialize: (startDayStored) => Number(startDayStored),
    serialize: (startDayStored) => startDayStored?.toString(),
  }),
  endDayStored: createUrlFilter({
    key: 'endDayStored',
    deserialize: (endDayStored) => Number(endDayStored),
    serialize: (endDayStored) => endDayStored?.toString(),
  }),
  storageFilter: createUrlEnumFilter<StorageFilterType>('storageFilter'),

  dailySelector: createUrlEnumFilter<DayMenuOption>('daily-selector'),
  unitReachStatus: createUrlEnumFilter<UnitReachStatus>(SEARCH_PARAM_KEYS.unitReachStatus),
  hitsTypes: createUrlFilter({
    key: 'hitsTypes',
    deserialize: (type) => (type ? type : SEARCH_PARAMS_VALUES.hitsTypes.all),
    serialize: (type) => type,
  }),
  userTypes: createUrlFilter({
    key: 'userTypes',
    deserialize: (type) => (type ? type : SEARCH_PARAMS_VALUES.userTypes.clients),
    serialize: (type) => type,
  }),
  from: createUrlFilter({
    key: 'from',
    deserialize: (from) => (from ? from : window.location.href),
    serialize: (from) => from,
  }),
  pageSize: createUrlFilter({
    key: 'pageSize',
    deserialize: (pageSize) => (isNaN(Number(pageSize)) ? PAGE_SIZE : Number(pageSize)),
    serialize: (pageSize) => (pageSize ? pageSize.toString() : undefined),
  }),
  spottedCasesStatus: createUrlFilter({
    key: 'spottedCasesStatus',
    deserialize: (status) => status,
    serialize: (status) => status,
  }),
  showExpired: createUrlFilter<boolean | undefined>({
    key: 'showExpired',
    deserialize: (show) => (show === '1' ? true : undefined),
    serialize: (show) => (show ? '1' : undefined),
  }),
  excludeInactiveUsers: createUrlFilter<boolean | undefined>({
    key: 'excludeInactiveUsers',
    deserialize: (value) => (value === undefined ? undefined : value === '1'),
    serialize: (value) => {
      if (value === undefined) return undefined;
      return value ? '1' : '0';
    },
  }),
  caseDateType: createUrlEnumFilter<DateRangeType>('caseDateType'),
  rangeFilter: createUrlEnumFilter<OrderTypeFilters>(SEARCH_PARAM_KEYS.rangeFilter),
  [SEARCH_PARAM_KEYS.peopleInShiftTab]: createUrlEnumFilter<PeopleInShiftsRole>(
    SEARCH_PARAM_KEYS.peopleInShiftTab,
  ),
  peopleInShiftsSortField: createUrlEnumFilter<PeopleInShiftsFields>('people-in-shifts-sort-field'),
  [SEARCH_PARAM_KEYS.dailyMapTab]: createUrlEnumFilter<DailyMapTabType>(SEARCH_PARAM_KEYS.dailyMapType),
  dateType: createUrlFilter<DateRangeType, DateRangeType>({
    key: 'dateType',
    deserialize: (dateType) => dateType as DateRangeType,
    serialize: (dateType) => dateType,
  }),
  reportsDateType: createUrlFilter<DateRangeType, DateRangeType>({
    key: 'reportsDateType',
    deserialize: (dateType) => dateType as DateRangeType,
    serialize: (dateType) => dateType,
  }),
  topEmployeeStatsDateRangeTab: createUrlEnumFilter<DateRangeType>('topEmployeeStatsDateRangeTab'),
  topAvgWeeklyHoursDateRangeTab: createUrlEnumFilter<DateRangeType>('topAvgWeeklyHoursDateRangeTab'),
  topIdleHoursDateRangeTab: createUrlEnumFilter<DateRangeType>('topIdleHoursDateRangeTab'),
  topDriveHoursDateRangeTab: createUrlEnumFilter<DateRangeType>('topDriveHoursDateRangeTab'),
  date: createUrlFilter({
    key: 'date',
    deserialize: (date) => {
      if (!date) return undefined;
      const parsed = DateTime.fromISO(date);
      return parsed.isValid ? parsed : undefined;
    },
    serialize: (date) => date?.toISO(),
  }),
  startDateTime: createUrlFilter({
    key: 'startDateTime',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  reportsStartDateTime: createUrlFilter({
    key: 'reportsStartDateTime',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  endDateTime: createUrlFilter({
    key: 'endDateTime',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date?: DateTime) => date?.toISO(),
  }),
  reportsEndDateTime: createUrlFilter({
    key: 'reportsEndDateTime',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date?: DateTime) => date?.toISO(),
  }),
  // duplicate of startDateTime but there are some calls with this key
  startDate: createUrlFilter({
    key: 'startDate',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  // duplicate of endDateTime but there are some calls with this key
  endDate: createUrlFilter({
    key: 'endDate',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date?: DateTime) => date?.toISO(),
  }),
  checklistStartDate: createUrlFilter({
    key: 'checklistStartDate',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  checklistEndDate: createUrlFilter({
    key: 'checklistEndDate',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  topEmployeeStatsStartDateTime: createUrlFilter({
    key: 'topEmployeeStatsStartDateTime',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  topEmployeeStatsEndDateTime: createUrlFilter({
    key: 'topEmployeeStatsEndDateTime',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  topEmployeeStatsSortKey: createUrlEnumFilter<TopEmployeeStatsField>('topEmployeeStatsSortKey'),
  exportStartDateTime: createUrlFilter({
    key: 'exportStartDateTime',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  exportEndDateTime: createUrlFilter({
    key: 'exportEndDateTime',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  users: createUrlFilter({
    key: 'users',
    deserialize: (users) => users?.split(',').map((id) => Number(id)) || [],
    serialize: (users) => (users ? users.join(',') : undefined),
  }),
  clients: createUrlFilter({
    key: 'clients',
    deserialize: (clients) => clients?.split(',') || [],
    serialize: (clients) => (clients ? clients.join(',') : undefined),
  }),
  page: createUrlFilter({
    key: 'page',
    deserialize: (page) => (isNaN(Number(page)) ? 1 : Number(page)),
    serialize: (page) => (page ? page.toString() : undefined),
  }),
  vin: createUrlFilter({
    key: 'vin',
    deserialize: (vin) => vin || '',
    serialize: (vin) => vin,
  }),
  branch: createUrlFilter({
    key: 'branch',
    deserialize: (branch) => (isNaN(Number(branch)) ? 0 : Number(branch)),
    serialize: (branch) => branch?.toString(),
  }),
  repoModal: createUrlFilter({
    key: 'repo-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  assignmentModal: createUrlFilter({
    key: 'assignment-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  missedRepoModal: createUrlFilter({
    key: 'missed-repo-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  spottedVehicleRefinedSearch: createUrlFilter({
    key: 'spotted-vehicle-refined-search',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  clientId: createUrlFilter({
    key: 'clientId',
    deserialize: (clientId) => (clientId === 'null' ? null : clientId),
    serialize: (clientId) => clientId,
  }),
  clientGroupId: createUrlFilter({
    key: 'clientGroupId',
    deserialize: (clientId) => (isNaN(Number(clientId)) ? undefined : Number(clientId)),
    serialize: (clientId) => clientId?.toString(),
  }),
  lienholderGroupId: createUrlFilter({
    key: 'lienholderGroupId',
    deserialize: (clientId) => (isNaN(Number(clientId)) ? undefined : Number(clientId)),
    serialize: (clientId) => clientId?.toString(),
  }),
  caseId: createUrlFilter({
    key: 'caseId',
    deserialize: (clientId) => clientId,
    serialize: (clientId) => clientId,
  }),
  clientName: createUrlFilter({
    key: 'clientName',
    deserialize: (clientName) => clientName,
    serialize: (clientName) => clientName,
  }),
  groupId: createUrlFilter({
    key: 'groupId',
    deserialize: (groupId) => Number(groupId),
    serialize: (groupId) => groupId?.toString(),
  }),
  groupName: createUrlFilter({
    key: 'groupName',
    deserialize: (groupName) => groupName,
    serialize: (groupName) => groupName,
  }),
  agentId: createUrlFilter({
    key: 'agentId',
    deserialize: (clientId) => clientId,
    serialize: (clientId) => clientId,
  }),
  userId: createUrlFilter({
    key: 'userId',
    deserialize: (userId) => Number(userId),
    serialize: (userId) => userId?.toString(),
  }),
  agentName: createUrlFilter({
    key: 'agentName',
    deserialize: (clientName) => clientName,
    serialize: (clientName) => clientName,
  }),
  lienholderId: createUrlFilter({
    key: 'lienholderId',
    deserialize: (clientId) => (clientId === 'null' ? null : clientId),
    serialize: (clientId) => clientId,
  }),
  lienholderName: createUrlFilter({
    key: 'lienholderName',
    deserialize: (clientName) => clientName,
    serialize: (clientName) => clientName,
  }),
  shiftCardActiveModal: createUrlFilter({
    key: 'shiftCardActiveModal',
    deserialize: (activeModal) => (activeModal ? (Number(activeModal) as ShiftCardActiveModal) : undefined),
    serialize: (activeModal?: ShiftCardActiveModal) => activeModal?.toString(),
  }),
  agentToggle: createUrlFilter({
    key: 'agentToggle',
    deserialize: (agentToggle) => agentToggle === SEARCH_PARAMS_VALUES.true,
    serialize: (agentToggle) => agentToggle?.toString(),
  }),
  // orderTypeTab: createUrlEnumFilter<OrderTypeCategory>('orderTypeTab', OrderTypeCategory.ALL),
  recoveryTab: createUrlFilter({
    key: 'recoveryTab',
    deserialize: (tab) =>
      RECOVERY_AGENTS_RANK_OPTIONS_V2.find((item) => item.value === Number(tab)) ||
      RECOVERY_AGENTS_RANK_OPTIONS_V2[0],
    serialize: (tab?: RecoveryTabs) => tab?.toString(),
  }),
  dailyModalType: createUrlEnumFilter<ReportDailyMetric>(SEARCH_PARAM_KEYS.dailyModalType),
  modalType: createUrlEnumFilter<ModalType>('modalType'),
  cameraCarsTab: createUrlFilter({
    key: 'cameraCarsTab',
    deserialize: (tab) =>
      CAMERA_CARS_RANK_OPTIONS_V2.find((item) => item.value === Number(tab)) || CAMERA_CARS_RANK_OPTIONS_V2[0],
    serialize: (tab?: CameraCarsTabs) => tab?.toString(),
  }),
  cameraCarsLiveHitsModal: createUrlObjectFilter<{
    firstName: string;
    lastName: string;
    id: string;
  }>('cameraCarsLiveHitsModal'),
  cameraCarsLiveHitsModalV2: createUrlObjectFilter<HitsDataModalPropV2>('cameraCarsLiveHitsModal'),
  cameraCarsSecuredModal: createUrlObjectFilter<{
    firstName: string;
    lastName: string;
    id: string;
  }>('cameraCarsSecuredModal'),
  cameraCarsSecuredModalV2: createUrlObjectFilter<SecuredDataModalPropV2>('cameraCarsSecuredModal'),
  topSpottersModal: createUrlObjectFilter<TopSpotterModalData>('topSpottersSecuredModal'),
  cameraHitsStatus: createUrlEnumFilter<CameraHitsStatus>('cameraHitsStatus'),
  cameraHitsType: createUrlEnumFilter<HitType>('cameraHitsType'),
  status: createUrlEnumFilter<CaseStatusFilter>('status'),
  spottersTab: createUrlEnumFilter<SpotType>('spottersTab', SpotType.TOTAL),

  spottersModal: createUrlFilter({
    key: 'spottersModal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  selectShiftModal: createUrlFilter({
    key: 'select-shift-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  manualTimeModal: createUrlFilter({
    key: 'manual-time-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  alertMotionModal: createUrlFilter({
    key: 'alert-motion-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  breakSelectionModal: createUrlFilter({
    key: 'break-selection-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  topRecoveryAgentsModal: createUrlObjectFilter<TopRecoveryAgentsModalProp>('topRecoveryAgentsModal'),
  recoveryModal: createUrlFilter({
    key: 'recovery-modal',
    deserialize: (ytd) => ytd === SEARCH_PARAMS_VALUES.true,
    serialize: (ytd) => ytd?.toString(),
  }),
  id: createUrlFilter({
    key: 'id',
    deserialize: (id) => Number(id),
    serialize: (id) => id?.toString(),
  }),
  firstName: createUrlFilter({
    key: SEARCH_PARAM_KEYS.firstName,
    deserialize: (value) => value,
    serialize: (value) => value,
  }),
  lastName: createUrlFilter({
    key: SEARCH_PARAM_KEYS.lastName,
    deserialize: (value) => value,
    serialize: (value) => value,
  }),
  rankOption: createUrlFilter({
    key: 'rankOption',
    deserialize: (rankOption) => (rankOption ? JSON.parse(rankOption) : {}),
    serialize: (rankOption) => (rankOption ? JSON.stringify(rankOption) : undefined),
  }),
  reportMenuOption: createUrlEnumFilter<ReportMenuOption>(SEARCH_PARAM_KEYS.viewType),
  clientPieChartTab: createUrlEnumFilter<DateRangeSelector>(SEARCH_PARAM_KEYS.clientPieChartTab),
  [SEARCH_PARAM_KEYS.viewTab]: createUrlEnumFilter<ViewTab>(SEARCH_PARAM_KEYS.viewTab),
  clientSelector: createUrlEnumFilter<ClientType>('client-selector'),
  clientListTab: createUrlEnumFilter<DateRangeSelector>(SEARCH_PARAM_KEYS.clientListTab),
  timeSelector: createUrlEnumFilter<DateRangeSelector>(SEARCH_PARAM_KEYS.timeSelector),
  year: createUrlFilter({
    key: SEARCH_PARAM_KEYS.year,
    deserialize: (year) => Number(year),
    serialize: (year) => (year ? year.toString() : DateTime.now().year.toString()),
  }),
  month: createUrlFilter({
    key: SEARCH_PARAM_KEYS.month,
    deserialize: (month) => Number(month),
    serialize: (month) =>
      month === undefined || month < 0 ? (DateTime.now().month - 1).toString() : month.toString(),
  }),
  ytd: createUrlFilter({
    key: SEARCH_PARAM_KEYS.ytd,
    deserialize: (ytd) => ytd === SEARCH_PARAMS_VALUES.true,
    serialize: (ytd) => ytd?.toString(),
  }),
  camerasTab: createUrlFilter({
    key: SEARCH_PARAM_KEYS.camerasTab,
    deserialize: (camerasTab) => Number(camerasTab) || 0,
    serialize: (camerasTab) => camerasTab?.toString(),
  }),
  camerasModal: createUrlFilter({
    key: SEARCH_PARAM_KEYS.camerasModal,
    deserialize: (camerasModal) => camerasModal === SEARCH_PARAMS_VALUES.true,
    serialize: (camerasModal) => camerasModal?.toString(),
  }),
  rankValue: createUrlFilter({
    key: SEARCH_PARAM_KEYS.rankValue,
    deserialize: (rankValue) => Number(rankValue),
    serialize: (rankValue) => rankValue?.toString(),
  }),
  rankLabel: createUrlFilter({
    key: SEARCH_PARAM_KEYS.rankLabel,
    deserialize: (rankLabel) => rankLabel,
    serialize: (rankLabel) => rankLabel,
  }),
  name: createUrlFilter({
    key: SEARCH_PARAM_KEYS.name,
    deserialize: (name) => name,
    serialize: (name) => name,
  }),
  hitStatus: createUrlFilter({
    key: SEARCH_PARAM_KEYS.hitStatus,
    deserialize: (hitStatus) => hitStatus,
    serialize: (hitStatus) => hitStatus,
  }),
  topRecoveryAgentDetails: createUrlObjectFilter<TopRecoveryAgentDetails>('topRecoveryAgentDetails'),
  checklistCategory: createUrlEnumFilter<ChecklistQuestionCategory>(
    'checklist-category',
    ChecklistQuestionCategory.NEW_HIRE,
  ),
  checklistTabValue: createUrlEnumFilter<ChecklistTabValueEnum>('checklist-category', ChecklistTabValue.NEW_HIRE),
  userRoleTypeNumber: createUrlFilter({
    key: 'user-role-type',
    deserialize: (id) => Number(id),
    serialize: (id) => String(id),
  }),
  userRoleType: createUrlEnumFilter<RolesType>('user-role-type'),
  userRoleId: createUrlFilter({
    key: 'user-role-id',
    deserialize: (id) => Number(id),
    serialize: (id) => String(id),
  }),
  createChecklistModal: createUrlFilter({
    key: 'create-checklist-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  deleteChecklistModal: createUrlFilter({
    key: 'delete-checklist-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  submitChecklistModal: createUrlFilter({
    key: 'submit-checklist-modal',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  submitChecklistModalCategory: createUrlEnumFilter<ChecklistSetCategory>('submit-checklist-modal-category'),
  checklistQuestionId: createUrlFilter({
    key: 'checklist-question-id',
    deserialize: (id) => Number(id),
    serialize: (id) => String(id),
  }),
  step: createUrlFilter({
    key: 'step',
    deserialize: (step) => Number(step),
    serialize: (step) => String(step),
  }),
  activeShiftsDashboard: createUrlObjectFilter<ActiveShiftsDashboard>('activeShiftsDashboard'),
  title: createUrlEnumFilter<'Top Camera Cars' | 'Top Recovery Agents' | 'Top Spotters'>('title'),
  dailyMapType: createUrlEnumFilter<DailyMapType>('daily-map-type', DailyMapType.TOTAL_REPOSESSIONS),
  timeSheetsViewType: createUrlEnumFilter<'list' | 'grid'>('timeSheetsViewType'),
  showAssignedCasesToMe: createUrlFilter<boolean>({
    key: 'showAssignedCasesToMe',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  adminTasks: createUrlObjectFilter<AdminTasksUrlFilters>('adminTasks'),
  managerTasks: createUrlObjectFilter<ManagerTasksUrlFilters>('managerTasks'),
  driverTasks: createUrlObjectFilter<DriverTasksUrlFilters>('driverTasks'),
  userSelectedTab: createUrlEnumFilter<UserDetailsTab>('userSelectedTab'),
  checklistFilter: createUrlEnumFilter<ChecklistFilter>('checklistFilter'),
  checklistDateType: createUrlEnumFilter<DateRangeType>('checklistDateType'),
  isAveragePerDay: createUrlFilter({
    key: 'is-average-per-day',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  fromReports: createUrlFilter({
    key: 'from-reports',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  fromClientDetails: createUrlFilter({
    key: 'from-client-details',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  fromYTDTopDrivers: createUrlFilter({
    key: 'from-ytd-top-drivers',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  fromEmployeeStats: createUrlFilter({
    key: 'from-employee-stats',
    deserialize: (show) => (show && Number(show) === 1 ? true : false),
    serialize: (show) => (show ? '1' : '0'),
  }),
  scansPerHit: createUrlEnumFilter<ScanOption>('scans-per-hit', ScanOption.TOTAL_SCANS),
  suscriptionView: createUrlEnumFilter<SuscriptionViews>('suscription-view', SuscriptionViews.SUSCRIPTION),
  updateSubscription: createUrlFilter<boolean>({
    key: 'updateSubscription',
    deserialize: (subscription) => subscription === SEARCH_PARAMS_VALUES.true,
    serialize: (subscription) => subscription?.toString(),
  }),
  userStatsDateRange: createUrlEnumFilter<UserStatsTabType>('user-stats-date-range', UserStatsTabType.TODAY),
  infractionHistoryStartDate: createUrlFilter({
    key: 'infraction-history-start-date',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  infractionHistoryEndDate: createUrlFilter({
    key: 'infraction-history-end-date',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  infractionHistoryDateType: createUrlEnumFilter<DateRangeType>('infraction-history-date-type'),
  infractionFeedStartDate: createUrlFilter({
    key: 'infraction-feed-start-date',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  infractionFeedEndDate: createUrlFilter({
    key: 'infraction-feed-end-date',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  infractionFeedDateType: createUrlEnumFilter<DateRangeType>('infraction-feed-date-type'),
  taskGroupSort: createUrlEnumFilter<TaskStatusGroupListOrderBy>('task-group-sort'),
  userTaskHistoryStartDate: createUrlFilter({
    key: 'user-task-history-start-date',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  userTaskHistoryEndDate: createUrlFilter({
    key: 'user-task-history-end-date',
    deserialize: (date) => (date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : undefined),
    serialize: (date) => date?.toISO(),
  }),
  userTaskDateType: createUrlEnumFilter<DateRangeType>('user-task-date-type'),
  peopleWorkingTab: createUrlFilter({
    key: 'peopleWorkingTab',
    deserialize: (tab) => tab,
    serialize: (tab) => tab,
  }),
  e2eTest: createUrlFilter<boolean>({
    key: 'e2e-test',
    deserialize: (value) => value === 'true',
    serialize: (value) => (value ? 'true' : undefined),
  }),

  infractionSortKey: createUrlEnumFilter<InfractionsSortKey>('infractionSortKey'),
  spottedVehicleSortKey: createUrlEnumFilter<SpottedVehicleReportSortKey>('spottedVehicleSortKey'),
  repossessionSortKey: createUrlEnumFilter<RepossessionsSortKey>('repossessionSortKey'),
  spottedVehiclesType: createUrlFilter({
    key: 'spottedVehiclesType',
    deserialize: (type) => type || 'spot',
    serialize: (type) => type,
  }),
};

export type FiltersObjectType = typeof filters;

type FiltersType = ReturnType<typeof createFilters>;

export type FiltersValuesType<T extends FiltersType> = {
  [K in keyof T]: ReturnType<T[K]['deserialize']>;
};
export type FiltersValuesTypeSet<T extends FiltersType> = {
  [K in keyof T]: Parameters<T[K]['serialize']>[0];
};

export type FiltersReturnType<V extends FiltersType, T extends (keyof V)[]> = {
  [K in T[number]]: FiltersValuesType<V>[K];
};

export type FiltersSetType<T extends FiltersType> = (
  set: Partial<{
    [K in keyof T]: FiltersValuesTypeSet<T>[K] | ((prev: FiltersValuesType<T>[K]) => FiltersValuesTypeSet<T>[K]);
  }>,
) => void;

export type FiltersSetTypeState<T extends FiltersType> = {
  push: FiltersSetType<T>;
  replace: FiltersSetType<T>;
  persist: FiltersSetType<T>;
};
